/**
 * Integrytas Group - Common Security Module (CSM)
 * TaskMonitor — docked bottom bar showing real-time background task progress.
 *
 * Behaviour:
 *   • Visible whenever there are tasks in the queue (active or recently done).
 *   • Collapses to a slim status chip when all tasks are complete.
 *   • Expands to a full panel listing each task with progress bar, elapsed
 *     time, status badge, and a cancel button for running tasks.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskQueue, type Task, type TaskStatus } from '../../lib/use_task_queue';

const STATUS_STYLES: Record<TaskStatus, { bg: string; text: string; label: string }> = {
  PENDING:   { bg: 'bg-csm-info-soft',    text: 'text-csm-info',    label: 'Queued'    },
  RUNNING:   { bg: 'bg-primary-soft',     text: 'text-primary',     label: 'Running'   },
  COMPLETED: { bg: 'bg-csm-success-soft', text: 'text-csm-success', label: 'Done'      },
  FAILED:    { bg: 'bg-csm-danger-soft',  text: 'text-csm-danger',  label: 'Failed'    },
  CANCELLED: { bg: 'bg-gray-100',         text: 'text-csm-muted',   label: 'Cancelled' },
};

function elapsed(createdAt: string): string {
  const ms = Date.now() - new Date(createdAt).getTime();
  const s  = Math.floor(ms / 1000);
  if (s < 60)  return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

function TaskRow({ task, onCancel }: { task: Task; onCancel: (id: number) => void }) {
  const { t } = useTranslation();
  const st = STATUS_STYLES[task.status];
  return (
    <div className="flex items-center gap-2 border-b border-csm-border px-3 py-2 last:border-0">
      {/* Status badge */}
      <span className={`shrink-0 rounded border px-1.5 py-0.5 text-csm-2xs font-semibold uppercase tracking-wide ${st.bg} ${st.text} border-transparent`}>
        {st.label}
      </span>

      {/* Label */}
      <span className="flex-1 truncate text-csm-sm text-csm-ink">
        {task.label ?? task.task_type}
      </span>

      {/* Progress bar (only when RUNNING) */}
      {task.status === 'RUNNING' && (
        <div className="w-24 shrink-0">
          <div className="h-1.5 rounded bg-csm-border-soft">
            <div
              className="h-1.5 rounded bg-primary transition-all"
              style={{ width: `${task.progress}%` }}
            />
          </div>
          <p className="mt-0.5 text-right text-csm-2xs text-csm-muted">
            {task.progress}%
          </p>
        </div>
      )}

      {/* Elapsed */}
      <span className="shrink-0 text-csm-2xs text-csm-muted w-8 text-right">
        {elapsed(task.created_at)}
      </span>

      {/* Error */}
      {task.error_message && (
        <span className="shrink-0 max-w-[140px] truncate text-csm-2xs text-csm-danger" title={task.error_message}>
          {task.error_message}
        </span>
      )}

      {/* Cancel */}
      {(task.status === 'PENDING' || task.status === 'RUNNING') && (
        <button
          type="button"
          onClick={() => onCancel(task.task_id)}
          className="shrink-0 text-csm-xs text-csm-muted hover:text-csm-danger"
          title={t('tasks.cancel_task', 'Cancel task')}
        >
          ✕
        </button>
      )}
    </div>
  );
}

export function TaskMonitor() {
  const { t }        = useTranslation();
  const { tasks, activeTasks, cancelTask } = useTaskQueue();
  const [expanded, setExpanded] = useState(false);

  if (tasks.length === 0) return null;

  const hasActive     = activeTasks.length > 0;
  const recentTasks   = tasks.slice(0, 10);

  return (
    <div className="shrink-0">
      {/* ── Expanded panel ────────────────────────────────────────────────── */}
      {expanded && (
        <div className="border-t border-csm-border bg-white shadow-[0_-2px_8px_rgba(0,0,0,0.08)]">
          <div className="flex items-center justify-between border-b border-csm-border px-3 py-1.5">
            <span className="text-csm-xs font-semibold uppercase tracking-wider text-csm-muted">
              {t('tasks.panel_title')}
            </span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-csm-muted hover:text-csm-ink text-csm-md"
            >
              ▼
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {recentTasks.map(t => (
              <TaskRow key={t.task_id} task={t} onCancel={cancelTask} />
            ))}
          </div>
        </div>
      )}

      {/* ── Collapsed status bar ──────────────────────────────────────────── */}
      <div
        className={[
          'flex h-7 shrink-0 cursor-pointer items-center gap-3 border-t px-3',
          hasActive
            ? 'border-primary/30 bg-primary-soft'
            : 'border-csm-border bg-csm-surface-alt',
        ].join(' ')}
        onClick={() => setExpanded(e => !e)}
        title={expanded ? t('tasks.collapse', 'Collapse tasks') : t('tasks.expand', 'Expand tasks')}
      >
        {/* Spinning indicator when active */}
        {hasActive && (
          <span className="h-2.5 w-2.5 shrink-0 animate-spin rounded-full border-2
                           border-primary border-t-transparent" />
        )}

        {/* Summary text */}
        <span className="text-csm-xs text-csm-ink-soft">
          {hasActive
            ? t('tasks.n_running', { count: activeTasks.length })
            : t('tasks.all_done', { count: tasks.filter(t => t.status === 'COMPLETED').length })}
        </span>

        {/* Active task label */}
        {activeTasks[0] && (
          <>
            <span className="text-csm-muted text-csm-xs">·</span>
            <span className="flex-1 truncate text-csm-xs text-primary">
              {activeTasks[0].label ?? activeTasks[0].task_type}
              {activeTasks[0].status === 'RUNNING' && ` (${activeTasks[0].progress}%)`}
            </span>
          </>
        )}

        <span className="ml-auto text-csm-xs text-csm-muted">{expanded ? '▼' : '▲'}</span>
      </div>
    </div>
  );
}
